/**
 * Sticky add-to-cart bar.
 * Added Sep 2026 for Vince. Not part of stock Venue.
 *
 * Shows once the real buy box has scrolled off the top, hides again when it
 * returns or when the footer comes into view. The button proxies a click to
 * the real Add to Cart button rather than submitting its own form, so all of
 * Venue's cart behaviour is reused and a double add is impossible.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.anchor = document.querySelector('.product-single__box__block--buy_buttons');
    this.realBtn = document.querySelector('.js-product-add');
    if (!this.anchor || !this.realBtn) return;

    // position:fixed is unreliable inside a transformed, filtered or
    // overflow-clipped ancestor. Reparenting to body sidesteps all of that.
    if (this.parentElement !== document.body) document.body.appendChild(this);

    this.btn = this.querySelector('[data-sticky-add]');
    this.btnText = this.querySelector('[data-sticky-add-text]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.metaEl = this.querySelector('[data-sticky-meta]');

    this.pastAnchor = false;
    this.footerVisible = false;

    this.btn.addEventListener('click', () => {
      const live = document.querySelector('.js-product-add');
      if (live && !live.disabled) live.click();
    });

    document.querySelectorAll('variant-radios, variant-selects, variant-mixed-inputs')
      .forEach((el) => el.addEventListener('on:variant:change', () => this.sync()));

    this.observe();
    this.sync();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  observe() {
    // IntersectionObserver is the wrong tool here. Scrolling from "buy box
    // below the viewport" to "buy box above the viewport" leaves isIntersecting
    // false at both ends, so a fast or jumped scroll fires no callback and the
    // bar keeps a stale state. Measuring on scroll is correct in every case.
    this.footer = document.querySelector('#footer, footer');
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.measure();
  }

  measure() {
    const box = this.anchor.getBoundingClientRect();
    this.pastAnchor = box.bottom < 0;
    this.footerVisible = this.footer
      ? this.footer.getBoundingClientRect().top < window.innerHeight
      : false;
    this.render();
  }

  render() {
    const show = this.pastAnchor && !this.footerVisible;
    if (show === this.shown) return;
    this.shown = show;
    document.documentElement.classList.toggle('has-sticky-atc', show);
    if (show) {
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-visible'));
    } else {
      this.classList.remove('is-visible');
      // keep it in the tree until the transition finishes, then hide from AT
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { if (!this.shown) this.hidden = true; }, 220);
    }
  }

  /** Mirror the live buy box: price, option, per-serve, availability, label. */
  sync() {
    const live = document.querySelector('.js-product-add');
    if (live) this.realBtn = live;

    const price = document.querySelector('.price__number .money, .price__number');
    if (price && this.priceEl) this.priceEl.textContent = price.textContent.trim();

    const checked = document.querySelector('.product-form__swatch__input:checked, .product-form__swatch select');
    const row = checked ? checked.closest('.product-form__swatch__item') : null;

    let option = '';
    if (checked) {
      const value = checked.value || '';
      option = value.split(' - ')[0].trim();
    }
    // the per-serve figure already rendered in the picker — read it rather
    // than recompute, so the two can never disagree
    const per = row ? row.querySelector('.vps__per [aria-hidden="true"]') : null;

    if (this.metaEl) {
      const parts = [];
      if (option && option !== 'Default Title') parts.push(option);
      if (per) parts.push(per.textContent.trim());
      this.metaEl.textContent = parts.join(' · ');
    }

    const disabled = this.realBtn ? this.realBtn.disabled : false;
    this.btn.disabled = disabled;
    const liveText = this.realBtn ? this.realBtn.querySelector('.staged-action__text') : null;
    if (liveText && this.btnText) this.btnText.textContent = liveText.textContent.trim();
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
